/* ==========================================================================
   JGP CUSTOM JEANS CONFIGURATOR — Jeans Gems Wholesale
   11-step wholesale personalisation engine + live cost estimator
   + layered visual preview (silhouette base × wash tint × artwork × monogram).
   Pairs with sections/custom-jeans-configurator.liquid + custom-jeans.css
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-cjc]');
  if (!root) return;

  /* ---------- Config from Liquid ---------- */
  var cfg = {};
  try { cfg = JSON.parse(root.getAttribute('data-cjc-config') || '{}'); } catch (e) { cfg = {}; }
  var CURRENCY = cfg.currencySymbol || '£';
  var BASE_CENTS = parseInt(cfg.basePriceCents, 10) || 0;
  var MOQ = parseInt(cfg.moq, 10) || 300;
  var QTY_STEP = parseInt(cfg.qtyStep, 10) || 10;
  var VARIANT_ID = cfg.variantId;
  var SURCHARGE_VARIANT_ID = cfg.surchargeVariantId || null;
  /* Pro-forma intake mode: production orders are submitted as a hidden £0.00
     order-request checkout (captures buyer contact details, creates a normal
     order for the tech-pack forwarder) — payment happens OFF-platform by bank
     transfer against the pro-forma invoice (50/50). Only the development
     sample is sold through regular paid checkout. */
  var ORDER_VARIANT_ID = cfg.orderVariantId || null;
  var PROFORMA = !!ORDER_VARIANT_ID;
  var DEPOSIT_PCT = parseInt(cfg.depositPct, 10) || 50;
  var TIERS = cfg.tiers && cfg.tiers.length ? cfg.tiers : [
    { qty: 300, off: 0 }, { qty: 500, off: 5 }, { qty: 1000, off: 10 }
  ];
  var OVERRIDES = cfg.priceOverrides || {};
  var ASSET_BASE = cfg.assetBase || window.JGP_ASSET_BASE || '';
  var TEMPLATES = cfg.templates || {};
  var PREVIEW = !!window.JGP_PREVIEW;

  /* ---------- Option catalog (backend term ⇢ user-end label) ---------- */
  var CATALOG = [
    {
      num: 1, title: 'Shape & Silhouette', sub: 'Your base cut — the pattern geometry of the jeans.',
      groups: [
        {
          id: 'body_geometry', label: 'Body Geometry', backend: 'Gender/Cut Base', type: 'cards',
          options: [
            { id: 'cut_masculine', label: 'Masculine Fit', backend: "Men's block", delta: 0 },
            { id: 'cut_feminine', label: 'Feminine Fit', backend: "Women's block", delta: 0 },
            { id: 'cut_universal', label: 'Universal / Fluid Fit', backend: 'Gender-neutral block', delta: 0 }
          ]
        },
        {
          id: 'leg_shape', label: 'Leg Shape', backend: 'Silhouette', type: 'cards', svg: 'leg',
          options: [
            { id: 'leg_skinny', label: 'Second Skin (Skinny)', backend: 'Skinny', delta: 0, svg: 'skinny',
              notFor: ['cut_universal'], notForReason: 'Skin-tight fits need a gender-specific block — closest on Universal / Fluid is Tailored (Slim).' },
            { id: 'leg_slim', label: 'Tailored (Slim)', backend: 'Slim', delta: 0, svg: 'slim' },
            { id: 'leg_straight', label: 'The Classic (Straight Leg)', backend: 'Straight', delta: 0, svg: 'straight' },
            { id: 'leg_taper', label: 'Athletic Taper', backend: 'Tapered', delta: 0, svg: 'taper' },
            { id: 'leg_flare', label: 'Retro Flare', backend: 'Bootcut / Flare', delta: 0, svg: 'flare' },
            { id: 'leg_wide', label: 'Relaxed & Roomy', backend: 'Wide-leg / Relaxed', delta: 0, svg: 'wide' }
          ]
        },
        {
          id: 'rise', label: 'Waistline Height', backend: 'Rise', type: 'cards',
          options: [
            { id: 'rise_low', label: 'Low Rise', desc: 'Sits on hips', backend: 'Low-rise', delta: 0 },
            { id: 'rise_mid', label: 'Natural Mid Rise', desc: 'Just below navel', backend: 'Mid-rise', delta: 0 },
            { id: 'rise_high', label: 'Classic High Rise', desc: 'At natural waist', backend: 'High-rise', delta: 0 },
            { id: 'rise_ultra', label: 'Ribcage Super High', desc: 'High above navel', backend: 'Ultra high-rise', delta: 0,
              notFor: ['cut_masculine', 'cut_universal'], notForReason: 'Feminine Fit only — ultra high-rise is drafted on the women’s pattern block.' }
          ]
        }
      ]
    },
    {
      num: 2, title: 'The Denim Foundation', sub: 'Texture, comfort level and the base colour of the fabric.',
      groups: [
        {
          id: 'stretch', label: 'Fabric Feel & Stretch', backend: 'Fabric stretch', type: 'cards',
          options: [
            { id: 'stretch_rigid', label: 'Vintage Rigid', desc: '100% cotton — breaks in beautifully', backend: '100% cotton rigid', delta: 0 },
            { id: 'stretch_comfort', label: 'Move-With-You Stretch', desc: '1–2% elastane, everyday comfort', backend: 'Comfort stretch 1–2%', delta: 0.60 },
            { id: 'stretch_ultra', label: 'Ultra-Flex Denim', desc: '3%+ stretch, legging-like feel', backend: 'High stretch 3%+', delta: 1.10 }
          ]
        },
        {
          id: 'wash', label: 'Choose Your Wash', backend: 'Denim colour & wash', type: 'swatches',
          options: [
            { id: 'wash_raw', label: 'Midnight Raw Indigo', backend: 'Raw / unwashed', delta: 0, swatch: 'linear-gradient(160deg,#1a2340,#0d1226)' },
            { id: 'wash_rinse', label: 'Deep Rinse Blue', backend: 'One-wash / rinse', delta: 0.40, swatch: 'linear-gradient(160deg,#243a6b,#16224a)' },
            { id: 'wash_medium', label: 'Everyday Classic Blue', backend: 'Medium wash', delta: 0.70, swatch: 'linear-gradient(160deg,#4a6da7,#33507f)' },
            { id: 'wash_light', label: '90s Vintage Sun-Bleached', backend: 'Light wash', delta: 1.10, swatch: 'linear-gradient(160deg,#8fabd4,#6f8fbe)' },
            { id: 'wash_black', label: 'Pitch Black', backend: 'Black denim', delta: 0.70, swatch: 'linear-gradient(160deg,#23232a,#0c0c10)' },
            { id: 'wash_grey', label: 'Washed Charcoal', backend: 'Grey denim', delta: 0.70, swatch: 'linear-gradient(160deg,#6b6f76,#43464c)' }
          ]
        }
      ]
    },
    {
      num: 3, title: 'Front Closure & Hardware Finish', sub: 'The functional hardware that anchors the front design.',
      groups: [
        {
          id: 'fly', label: 'Front Fly Style', backend: 'Fly closure', type: 'cards',
          options: [
            { id: 'fly_zip', label: 'Modern Zipper Fly', backend: 'Zipper fly (YKK)', delta: 0 },
            { id: 'fly_button', label: 'Heritage Button Fly', backend: 'Button fly', delta: 0.35 }
          ]
        },
        {
          id: 'hardware', label: 'Metal Button & Rivet Color', backend: 'Hardware finish', type: 'swatches',
          options: [
            { id: 'hw_brass', label: 'Vintage Brass', backend: 'Antique brass', delta: 0, swatch: 'radial-gradient(circle at 30% 30%,#d9b36a,#8c6a2f)' },
            { id: 'hw_copper', label: 'Classic Heritage Copper', backend: 'Copper', delta: 0.15, swatch: 'radial-gradient(circle at 30% 30%,#d98e5f,#8f4f2c)' },
            { id: 'hw_silver', label: 'Modern Polished Silver', backend: 'Shiny chrome', delta: 0.15, swatch: 'radial-gradient(circle at 30% 30%,#e8eaee,#9aa0ab)' },
            { id: 'hw_black', label: 'Minimalist Stealth Black', backend: 'Matte black', delta: 0.25, swatch: 'radial-gradient(circle at 30% 30%,#3a3a40,#121216)' }
          ]
        }
      ]
    },
    {
      num: 4, title: 'Stitching & Branding', sub: 'Structural lines and back-branding accents.',
      groups: [
        {
          id: 'thread', label: 'Stitching Accent Color', backend: 'Thread colour', type: 'swatches',
          options: [
            { id: 'thread_gold', label: 'Classic Denim Gold', backend: 'Contrast orange / tobacco', delta: 0, swatch: '#d4941e' },
            { id: 'thread_tonal', label: 'Invisible Stitching', desc: 'Matches the fabric colour', backend: 'Tonal thread', delta: 0, swatch: 'linear-gradient(135deg,#39415c,#2a3049)' },
            { id: 'thread_white', label: 'Pop Color — Electric White', backend: 'Bold statement: white', delta: 0.20, swatch: '#f4f4f4' },
            { id: 'thread_red', label: 'Pop Color — Crimson Red', backend: 'Bold statement: crimson', delta: 0.20, swatch: '#b3261e' },
            { id: 'thread_pink', label: 'Pop Color — Neon Pink', backend: 'Bold statement: neon pink', delta: 0.20, swatch: '#ff2e93' }
          ]
        },
        {
          id: 'patch', label: 'Back Waistband Patch', backend: 'Waistband patch material', type: 'cards',
          options: [
            { id: 'patch_jacron', label: 'Minimalist Craft Paper', backend: 'Jacron', delta: 0 },
            { id: 'patch_leather', label: 'Premium Tan Leather', backend: 'Genuine leather', delta: 0.55 },
            { id: 'patch_suede', label: 'Eco-Suede Patch', backend: 'Vegan suede', delta: 0.40 },
            { id: 'patch_none', label: 'Clean & Patchless', backend: 'No patch', delta: 0 }
          ]
        }
      ]
    },
    {
      num: 5, title: 'Pockets & Hem Details', sub: 'Architectural choices for the edges and storage.',
      groups: [
        {
          id: 'hem', label: 'Ankle Hem Style', backend: 'Hem finish', type: 'cards',
          options: [
            { id: 'hem_clean', label: 'Clean Tailored Hem', backend: 'Standard hem', delta: 0 },
            { id: 'hem_chain', label: 'Premium Vintage Rolled Hem', backend: 'Chain-stitched hem', delta: 0.45 },
            { id: 'hem_raw', label: 'Distressed Edge / Raw Cut', backend: 'Raw / frayed hem', delta: 0.30 },
            { id: 'hem_shadow', label: 'Shadow Hem', desc: 'Unfolded post-wash', backend: 'Released hem', delta: 0.35 }
          ]
        },
        {
          id: 'front_pocket', label: 'Front Pocket Shape', backend: 'Front pocket style', type: 'cards',
          options: [
            { id: 'fpocket_scoop', label: 'Traditional Scoop', backend: 'Standard scoop', delta: 0 },
            { id: 'fpocket_western', label: 'Horizontal Cowboy Pocket', backend: 'Western', delta: 0.30 },
            { id: 'fpocket_slant', label: 'Sleek Slant Pocket', backend: 'Trouser slant', delta: 0.25 }
          ]
        }
      ]
    },
    {
      num: 6, title: 'Lived-In Distressing', sub: 'Instant character and age for the denim.',
      groups: [
        {
          id: 'fading', label: 'Natural Fading Levels', backend: 'Fading effects', type: 'cards',
          options: [
            { id: 'fade_none', label: 'Solid & Pristine', backend: 'No fading', delta: 0 },
            { id: 'fade_worn', label: 'Naturally Broken-In', desc: 'Whiskers & honeycombs', backend: 'Whiskers / honeycombs', delta: 0.90 }
          ]
        },
        {
          id: 'rips', label: 'Rips & Shredding', backend: 'Destruction level', type: 'cards',
          options: [
            { id: 'rips_none', label: 'Clean (No Rips)', backend: 'None', delta: 0 },
            { id: 'rips_light', label: 'Softly Scuffed', desc: 'Pocket-edge & hem fraying', backend: 'Light fraying', delta: 0.60 },
            { id: 'rips_medium', label: 'Destructed Knees', desc: 'Clean open knee slashes', backend: 'Medium rips', delta: 1.10 },
            { id: 'rips_heavy', label: 'Fully Trashed & Rocker-Worn', desc: 'Heavy abrasions and holes', backend: 'Heavy shredding', delta: 1.90 }
          ]
        }
      ]
    },
    {
      num: 7, title: 'Custom Prints & Laser Art', sub: 'Surface design via modern printing and etching — upload your own artwork and preview it on the jeans.',
      groups: [
        {
          id: 'surface_art', label: 'Denim Surface Art', backend: 'Surface graphics', type: 'cards',
          options: [
            { id: 'art_none', label: 'None — Pure Denim', backend: 'No surface graphics', delta: 0 },
            { id: 'art_laser_mono', label: 'Laser-Etched Monogram', desc: 'Repeated subtle initials', backend: 'Laser etching: monogram', delta: 1.20, artwork: 'laser' },
            { id: 'art_tattoo', label: 'Tattoo Print', desc: 'Laser-etched floral / botanical', backend: 'Laser etching: pattern', delta: 1.40, artwork: 'laser' },
            { id: 'art_dtg', label: 'Thigh/Calf Graphic Print', desc: 'Bold direct-ink graphics', backend: 'DTG placement graphic', delta: 1.60, artwork: 'dtg' },
            { id: 'art_foil_gold', label: 'Liquid Metal Coating — Gold', backend: 'Metallic foil stamping: gold', delta: 1.80, artwork: 'foil_gold' },
            { id: 'art_foil_silver', label: 'Liquid Metal Coating — Silver', backend: 'Metallic foil stamping: silver', delta: 1.80, artwork: 'foil_silver' },
            { id: 'art_bleach', label: 'Artisanal Studio Bleach Splatter', desc: 'Hand-applied — no artwork needed', backend: 'Bleach splatter', delta: 1.30 }
          ]
        }
      ]
    },
    {
      num: 8, title: 'Sparkle & Shine', sub: 'Catching the light with dimensional attachments.',
      groups: [
        {
          id: 'crystals', label: 'Bling & Crystals', backend: 'Crystal accents', type: 'cards',
          options: [
            { id: 'bling_none', label: 'No Crystals', backend: 'None', delta: 0 },
            { id: 'bling_dust', label: 'Diamond Dust Scattered Crystals', desc: 'Front thighs', backend: 'Hotfix rhinestones', delta: 2.20 },
            { id: 'bling_disco', label: 'Disco Pocket Trims', desc: 'Rhinestones outlining back pockets', backend: 'Pocket rhinestones', delta: 1.60 },
            { id: 'bling_fringe', label: 'Crystal Shimmer Fringe', desc: 'Swinging fringe on hem or back yoke', backend: 'Rhinestone fringe', delta: 2.80,
              notFor: ['cut_masculine'], notForReason: 'Womenswear embellishment — available on Feminine and Universal / Fluid fits.' }
          ]
        },
        {
          id: 'studs', label: 'Heavy Metal Studs', backend: 'Metallic studding', type: 'cards',
          options: [
            { id: 'studs_none', label: 'No Studs', backend: 'None', delta: 0 },
            { id: 'studs_punk', label: "Rock 'N' Roll Punk Studs", desc: 'Along the outer side-seams', backend: 'Dome / pyramid studs', delta: 1.70 }
          ]
        }
      ]
    },
    {
      num: 9, title: 'Textures & Pretty Trims', sub: 'High-fashion textures and mixed-material details.',
      groups: [
        {
          id: 'underlay', label: 'Peek-a-Boo Fabric Underlays', backend: 'Fabric inserts', type: 'cards',
          lockedBy: { group: 'rips', not: 'rips_none', message: 'Unlocks when a rip level is selected in Step 6.' },
          options: [
            { id: 'under_none', label: 'No Underlay', backend: 'None', delta: 0 },
            { id: 'under_lace', label: 'Romantic Lace Backing', backend: 'Lace underlay', delta: 1.20 },
            { id: 'under_fishnet', label: 'Grunge Fishnet Backing', backend: 'Fishnet underlay', delta: 0.90 },
            { id: 'under_cheetah', label: 'Wildside Cheetah Backing', backend: 'Animal print underlay', delta: 1.10 }
          ]
        },
        {
          id: 'trims', label: 'Novelty Side Trims', backend: 'Trim accents', type: 'cards',
          options: [
            { id: 'trim_none', label: 'No Side Trim', backend: 'None', delta: 0 },
            { id: 'trim_corset', label: 'Corset-Style Ribbon Lace-Up', desc: 'Down the outer leg', backend: 'Ribbon side-tie', delta: 1.90,
              notFor: ['cut_masculine'], notForReason: 'Womenswear construction detail — available on Feminine and Universal / Fluid fits.' },
            { id: 'trim_tuxedo', label: 'Side-Seam Lace Tuxedo Stripe', backend: 'Lace panel', delta: 1.40 }
          ]
        },
        {
          id: 'waist_button', label: 'Jeweled Waist Button Upgrade', backend: 'Statement buttons', type: 'cards',
          options: [
            { id: 'wbtn_std', label: 'Standard Metal Button', backend: 'Standard shank button', delta: 0 },
            { id: 'wbtn_pearl', label: 'Mother of Pearl Button', backend: 'Faux-pearl button', delta: 0.45 },
            { id: 'wbtn_jewel', label: 'Diamond Jewel Button', backend: 'Rhinestone button', delta: 0.60 }
          ]
        }
      ]
    },
    {
      num: 10, title: 'Hidden Personalization', sub: 'Secret details that make the run truly bespoke — watch your monogram appear on the preview.',
      groups: [
        {
          id: 'monogram', label: 'Custom Monogramming', backend: 'Custom embroidery', type: 'monogram',
          maxChars: 12, delta: 0.85,
          placements: [
            { id: 'mono_waistband', label: 'Inside Waistband' },
            { id: 'mono_pocket', label: 'Pocket Lip' },
            { id: 'mono_ankle', label: 'Ankle Cuff' }
          ]
        }
      ]
    },
    {
      num: 11, title: 'Production Fit & Size Curve', sub: 'The final gate before checkout — set the base fit, then spread your run across sizes.',
      groups: [
        { id: 'sizing', label: 'How should we establish your production fit?', backend: 'Sizing methodology', type: 'sizing' }
      ]
    }
  ];

  CATALOG.forEach(function (step) {
    step.groups.forEach(function (g) {
      (g.options || []).forEach(function (o) {
        if (Object.prototype.hasOwnProperty.call(OVERRIDES, o.id)) o.delta = parseFloat(OVERRIDES[o.id]) || 0;
      });
      if (g.type === 'monogram' && Object.prototype.hasOwnProperty.call(OVERRIDES, 'monogram')) {
        g.delta = parseFloat(OVERRIDES.monogram) || 0;
      }
    });
  });

  var LEG_SVGS = {
    skinny:   'M20 4 H44 L42 30 40 60 37 92 H33 L32 62 32 34 30 62 31 92 H27 L24 60 22 30 Z',
    slim:     'M19 4 H45 L43 30 41 60 39 92 H33 L32 62 32 34 30 62 31 92 H25 L23 60 21 30 Z',
    straight: 'M18 4 H46 L45 32 44 92 H34 L32 40 30 92 H20 L19 32 Z',
    taper:    'M18 4 H46 L45 34 40 92 H34 L32 40 30 92 H24 L19 34 Z',
    flare:    'M19 4 H45 L43 34 41 58 46 92 H32 L32 44 32 92 H18 L23 58 21 34 Z',
    wide:     'M16 4 H48 L48 36 49 92 H34 L32 42 30 92 H15 L16 36 Z'
  };
  function legSvg(kind) {
    var d = LEG_SVGS[kind];
    if (!d) return '';
    return '<svg class="cjc-legsvg" viewBox="0 0 64 96" aria-hidden="true"><path d="' + d + '" /></svg>';
  }

  var WASH_FILTERS = {
    wash_raw:    'brightness(0.62) saturate(1.25) hue-rotate(6deg)',
    wash_rinse:  'brightness(0.78) saturate(1.15)',
    wash_medium: 'none',
    wash_light:  'brightness(1.22) saturate(0.72)',
    wash_black:  'brightness(0.38) saturate(0.12)',
    wash_grey:   'brightness(0.92) saturate(0.08)'
  };
  var WASH_TINTS = {
    wash_raw: '#1a2340', wash_rinse: '#243a6b', wash_medium: '#4a6da7',
    wash_light: '#8fabd4', wash_black: '#17171c', wash_grey: '#5a5e66'
  };
  var THREAD_COLORS = {
    thread_gold: '#d4941e', thread_tonal: '#5d688c', thread_white: '#f4f4f4',
    thread_red: '#c9362d', thread_pink: '#ff2e93'
  };
  var ART_PLACEMENTS = [
    { id: 'front_thigh', label: 'Front thigh', backend: 'Front thigh panel (approx. 22 × 28 cm max)', x: 33, y: 38, w: 26, gx: 0.30, gy: 0.34, gw: 0.30, template: 'front_thigh' },
    { id: 'calf', label: 'Calf / lower leg', backend: 'Lower-leg panel (approx. 18 × 22 cm max)', x: 34, y: 70, w: 22, gx: 0.29, gy: 0.74, gw: 0.24, template: 'calf' },
    { id: 'full_leg', label: 'Full leg panel', backend: 'Full front-leg panel (approx. 30 × 75 cm max)', x: 33, y: 52, w: 32, gx: 0.29, gy: 0.55, gw: 0.36, template: 'full_leg' }
  ];
  var MONO_ANCHORS = {
    mono_waistband: { x: 50, y: 8,  gx: 0.50, gy: 0.05, size: 1.0, tag: 'inside waistband' },
    mono_pocket:    { x: 64, y: 24, gx: 0.73, gy: 0.20, size: 0.8, tag: 'pocket lip' },
    mono_ankle:     { x: 39, y: 90, gx: 0.31, gy: 0.94, size: 0.8, tag: 'ankle cuff' }
  };
  var DETAIL_ANCHORS = {
    body_geometry: { gx: 0.50, gy: 0.16, x: 50, y: 20 },
    rise:          { gx: 0.50, gy: 0.05, x: 50, y: 10 },
    stretch:       { gx: 0.38, gy: 0.35, x: 38, y: 38 },
    fly:           { gx: 0.50, gy: 0.13, x: 50, y: 18 },
    hardware:      { gx: 0.53, gy: 0.09, x: 52, y: 14 },
    thread:        { gx: 0.72, gy: 0.42, x: 66, y: 45 },
    patch:         { gx: 0.70, gy: 0.06, x: 64, y: 11 },
    hem:           { gx: 0.36, gy: 0.94, x: 38, y: 88 },
    front_pocket:  { gx: 0.68, gy: 0.15, x: 62, y: 20 },
    fading:        { gx: 0.40, gy: 0.34, x: 40, y: 37 },
    rips:          { gx: 0.37, gy: 0.55, x: 38, y: 55 },
    surface_art:   { gx: 0.35, gy: 0.36, x: 36, y: 39 },
    crystals:      { gx: 0.33, gy: 0.32, x: 35, y: 35 },
    studs:         { gx: 0.72, gy: 0.50, x: 66, y: 52 },
    underlay:      { gx: 0.37, gy: 0.56, x: 38, y: 56 },
    trims:         { gx: 0.72, gy: 0.45, x: 66, y: 48 },
    waist_button:  { gx: 0.52, gy: 0.09, x: 52, y: 14 }
  };

  var state = {};
  function freshState() {
    var s = { selections: {}, monogramText: '', monogramPlacement: 'mono_waistband',
      sizingMethod: null, sizingFields: {}, sizingUnit: 'cm',
      sizeRunMode: 'custom', sizeRunQty: {},
      artwork: null,
      adjustNotes: null, qty: MOQ, touched: {} };
    CATALOG.forEach(function (step) {
      step.groups.forEach(function (g) {
        if (g.options && g.options.length) s.selections[g.id] = g.options[0].id;
      });
    });
    return s;
  }
  state = freshState();

  function findGroup(gid) {
    for (var i = 0; i < CATALOG.length; i++) {
      var gs = CATALOG[i].groups;
      for (var j = 0; j < gs.length; j++) if (gs[j].id === gid) return gs[j];
    }
    return null;
  }
  function findOption(g, oid) {
    for (var i = 0; i < (g.options || []).length; i++) if (g.options[i].id === oid) return g.options[i];
    return null;
  }
  function groupLocked(g) {
    if (!g.lockedBy) return false;
    return state.selections[g.lockedBy.group] === g.lockedBy.not;
  }
  function selectedArtOption() {
    var g = findGroup('surface_art');
    return findOption(g, state.selections.surface_art);
  }

  var CONFLICT_RULES = [
    { a: { group: 'rips', options: ['rips_heavy'] },
      b: { group: 'surface_art', options: ['art_laser_mono', 'art_tattoo', 'art_dtg', 'art_foil_gold', 'art_foil_silver'] },
      reason: 'heavy shredding leaves no intact panel — prints, laser art and foil would sit across open holes.' },
    { a: { group: 'rips', options: ['rips_heavy'] },
      b: { group: 'crystals', options: ['bling_dust'] },
      reason: 'scattered thigh crystals need intact thigh fabric, which heavy shredding removes.' },
    { a: { group: 'surface_art', options: ['art_foil_gold', 'art_foil_silver'] },
      b: { group: 'crystals', options: ['bling_dust'] },
      reason: 'hot-fix crystals do not bond reliably on top of the metallic foil coating (same thigh panels).' }
  ];

  function fullLegBlocked() {
    return state.selections.rips === 'rips_medium' || state.selections.rips === 'rips_heavy';
  }

  function stepNumOf(gid) {
    for (var i = 0; i < CATALOG.length; i++) {
      for (var j = 0; j < CATALOG[i].groups.length; j++) {
        if (CATALOG[i].groups[j].id === gid) return CATALOG[i].num;
      }
    }
    return 0;
  }

  function applyConflicts(gid) {
    var notes = [];
    CONFLICT_RULES.forEach(function (rule) {
      [[rule.a, rule.b], [rule.b, rule.a]].forEach(function (pair) {
        var just = pair[0], other = pair[1];
        if (just.group !== gid) return;
        if (just.options.indexOf(state.selections[just.group]) === -1) return;
        if (other.options.indexOf(state.selections[other.group]) === -1) return;
        var og = findGroup(other.group);
        var prev = findOption(og, state.selections[other.group]);
        state.selections[other.group] = og.options[0].id;
        notes.push('“' + prev.label + '” (Step ' + stepNumOf(other.group) + ') was removed — ' + rule.reason);
      });
    });
    if (state.artwork && state.artwork.placement === 'full_leg' && fullLegBlocked()) {
      state.artwork.placement = 'front_thigh';
      notes.push('Artwork moved from “Full leg panel” to “Front thigh” — the full-leg panel crosses the knee destruction zone.');
    }
    return notes;
  }

  function optionRestricted(g, o) {
    if (g.id === 'body_geometry' || !o.notFor) return false;
    return o.notFor.indexOf(state.selections.body_geometry) !== -1;
  }
  function applyCutRestrictions() {
    var notes = [];
    var cut = findOption(findGroup('body_geometry'), state.selections.body_geometry);
    CATALOG.forEach(function (step) {
      step.groups.forEach(function (g) {
        if (!g.options) return;
        var sel = findOption(g, state.selections[g.id]);
        if (!sel || !optionRestricted(g, sel)) return;
        var fallback = null;
        for (var i = 0; i < g.options.length; i++) {
          if (!optionRestricted(g, g.options[i])) { fallback = g.options[i]; break; }
        }
        if (!fallback) return;
        state.selections[g.id] = fallback.id;
        notes.push('“' + sel.label + '” (Step ' + stepNumOf(g.id) + ') is not available with “' + cut.label + '” — switched to “' + fallback.label + '”.');
      });
    });
    return notes;
  }

  function conflictHint(g, o) {
    var hint = null;
    CONFLICT_RULES.forEach(function (rule) {
      [[rule.a, rule.b], [rule.b, rule.a]].forEach(function (pair) {
        if (pair[0].group === g.id && pair[0].options.indexOf(o.id) !== -1 &&
            pair[1].options.indexOf(state.selections[pair[1].group]) !== -1 &&
            state.selections[g.id] !== o.id) {
          var other = findOption(findGroup(pair[1].group), state.selections[pair[1].group]);
          hint = 'Replaces “' + other.label + '” (Step ' + stepNumOf(pair[1].group) + ') — ' + rule.reason;
        }
      });
    });
    return hint;
  }

  var SIZING_METHODS = [
    {
      id: 'clone', label: 'The Clone Fit (Golden Sample)', backend: 'Reference-garment measurements (base-size pattern)',
      desc: 'Measure your best-selling pair of jeans — we reverse-engineer the pattern and use it as the base size for the whole run.',
      fields: [
        { id: 'waist_flat', label: 'Waistband width (flat)' }, { id: 'front_rise', label: 'Front rise' },
        { id: 'back_rise', label: 'Back rise' }, { id: 'thigh_flat', label: 'Thigh width (flat)' },
        { id: 'knee_flat', label: 'Knee width (flat)' }, { id: 'leg_opening', label: 'Leg opening (flat)' },
        { id: 'inseam', label: 'Inseam length' }, { id: 'outseam', label: 'Outseam length' }
      ]
    },
    {
      id: 'body', label: 'The Fit-Model Profile', backend: 'Fit-model body measurements + ease preference (base size)',
      desc: "Define your target customer's body measurements (your fit model / customer avatar) — we draft the base-size pattern around them.",
      fields: [
        { id: 'body_waist', label: 'Natural waist' }, { id: 'body_hip', label: 'Hip (fullest point)' },
        { id: 'body_thigh', label: 'Thigh circumference' }, { id: 'body_inseam', label: 'Inseam (crotch to floor)' },
        { id: 'body_height', label: 'Height' }
      ],
      choice: { id: 'fit_pref', label: 'Fit preference', options: ['Tight', 'Standard', 'Loose'] }
    },
    {
      id: 'hybrid', label: 'The Standard Curve', backend: 'Standard retail base size + build adjustment',
      desc: 'Start from a standard retail base size with industry-standard grading — optionally adjusted for your customer base’s build.',
      selects: [
        { id: 'std_waist', label: 'Base waist size', options: ['24','25','26','27','28','29','30','31','32','33','34','36','38','40','42','44'] },
        { id: 'std_length', label: 'Base length', options: ['26','28','30','32','34','36'] }
      ],
      choice: { id: 'build_adj', label: 'Build adjustment', options: ['Standard build', 'Thicker Athletic Thigh Build', 'Narrower Waist Gap Build'] }
    }
  ];

  var SIZE_SYSTEMS = {
    feminine: {
      label: "UK women's sizes",
      sizes: ['UK 6', 'UK 8', 'UK 10', 'UK 12', 'UK 14', 'UK 16', 'UK 18', 'UK 20'],
      balanced: [5, 11, 18, 22, 18, 13, 8, 5],
      core:     [3, 10, 22, 28, 22, 10, 3, 2]
    },
    waist: {
      label: 'Waist sizes',
      sizes: ['W28', 'W30', 'W32', 'W34', 'W36', 'W38', 'W40'],
      balanced: [7, 16, 24, 24, 15, 9, 5],
      core:     [4, 13, 29, 30, 15, 6, 3]
    }
  };
  function sizeSystem() {
    return state.selections.body_geometry === 'cut_feminine' ? SIZE_SYSTEMS.feminine : SIZE_SYSTEMS.waist;
  }
  function distributeRun(qty, weights) {
    var total = weights.reduce(function (a, b) { return a + b; }, 0);
    var raw = weights.map(function (w) { return qty * w / total; });
    var out = raw.map(Math.floor);
    var used = out.reduce(function (a, b) { return a + b; }, 0);
    var rem = raw.map(function (r, i) { return { i: i, frac: r - out[i] }; })
      .sort(function (a, b) { return b.frac - a.frac; });
    for (var k = 0; used < qty; k = (k + 1) % rem.length) { out[rem[k].i]++; used++; }
    return out;
  }
  function factoryRun() {
    var sys = sizeSystem();
    var run = distributeRun(state.qty, sys.balanced);
    var map = {};
    sys.sizes.forEach(function (s, i) { map[s] = run[i]; });
    return map;
  }
  function customRunTotal() {
    var sys = sizeSystem(), sum = 0;
    sys.sizes.forEach(function (s) { sum += parseInt(state.sizeRunQty[s], 10) || 0; });
    return sum;
  }
  function sizeRunValid() {
    if (state.sizeRunMode === 'factory') return true;
    return customRunTotal() === state.qty;
  }
  function activeRun() {
    if (state.sizeRunMode === 'factory') return factoryRun();
    var sys = sizeSystem(), map = {};
    sys.sizes.forEach(function (s) { var v = parseInt(state.sizeRunQty[s], 10) || 0; if (v > 0) map[s] = v; });
    return map;
  }

  function money(n) {
    return CURRENCY + n.toFixed(2).replace(/\\B(?=(\\d{3})+(?!\\d))/g, ',');
  }
  function tierFor(qty) {
    var t = TIERS[0];
    TIERS.forEach(function (x) { if (qty >= x.qty && x.qty >= t.qty) t = x; });
    return t;
  }

  function computePrice() {
    var base = BASE_CENTS / 100;
    var surcharge = 0;
    var lines = [];
    CATALOG.forEach(function (step) {
      step.groups.forEach(function (g) {
        if (g.type === 'monogram') {
          if (state.monogramText.trim().length > 0) {
            surcharge += g.delta;
            lines.push({ label: 'Custom Monogramming', delta: g.delta });
          }
          return;
        }
        if (!g.options || groupLocked(g)) return;
        var o = findOption(g, state.selections[g.id]);
        if (o && o.delta > 0) { surcharge += o.delta; lines.push({ label: o.label, delta: o.delta }); }
      });
    });
    var unitRaw = base + surcharge;
    var tier = tierFor(state.qty);
    var unit = unitRaw * (1 - tier.off / 100);
    var total = unit * state.qty;
    return { base: base, surcharge: surcharge, lines: lines, unitRaw: unitRaw, unit: unit,
      tier: tier, total: total, deposit: total * DEPOSIT_PCT / 100 };
  }

  var stepsEl = root.querySelector('[data-cjc-steps]');
  var summaryEl = root.querySelector('[data-cjc-summary]');
  var priceEl = root.querySelector('[data-cjc-price]');
  var progressFill = root.querySelector('[data-cjc-progress-fill]');
  var progressText = root.querySelector('[data-cjc-progress-text]');
  var msgEl = root.querySelector('[data-cjc-msg]');
  var addBtn = root.querySelector('[data-cjc-add]');
  var qtyInput = root.querySelector('[data-cjc-qty]');
  var mobileBar = root.querySelector('[data-cjc-mobilebar]');
  var mediaEl = root.querySelector('[data-cjc-media]') || root.querySelector('.cjc-media');
  var stepperEl = root.querySelector('[data-cjc-stepper]');

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function deltaTag(delta) {
    if (!delta) return '<span class="cjc-opt__delta cjc-opt__delta--inc">Included</span>';
    return '<span class="cjc-opt__delta">+' + money(delta) + '/pc</span>';
  }

  var JEANS_OUTLINE = 'M14 4 H50 L52 30 54 92 H38 L32 44 26 92 H10 L12 30 Z';
  var stage = null;
  function buildStage() {
    if (!mediaEl) return;
    mediaEl.innerHTML =
      '<div class="cjc-stage" data-cjc-stage>' +
        '<img class="cjc-stage__base" data-cjc-stage-base alt="Custom jeans preview" style="display:none">' +
        '<div class="cjc-stage__fallback" data-cjc-stage-fallback>' +
          '<svg viewBox="0 0 64 96" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
            '<defs><clipPath id="cjcGarmentClip"><path d="' + JEANS_OUTLINE + '"/></clipPath></defs>' +
            '<path data-cjc-stage-path d="' + JEANS_OUTLINE + '" fill="#4a6da7" stroke="rgba(0,0,0,0.35)" stroke-width="1.5" stroke-linejoin="round"/>' +
            '<path d="M14 14 H50 M20 4 L22 14 M44 4 L42 14 M17 16 Q22 22 27 16 M37 16 Q42 22 47 16" fill="none" data-cjc-stage-stitch stroke="#d4941e" stroke-width="1" stroke-dasharray="2.5 2.5"/>' +
            '<image data-cjc-stage-fallback-art clip-path="url(#cjcGarmentClip)" preserveAspectRatio="xMidYMid meet" style="display:none" opacity="0.95"/>' +
          '</svg>' +
        '</div>' +
        '<img class="cjc-stage__art" data-cjc-stage-art alt="" style="display:none">' +
        '<span class="cjc-stage__mono" data-cjc-stage-mono style="display:none"><i data-cjc-stage-mono-text></i><em data-cjc-stage-mono-tag></em></span>' +
        '<div class="cjc-stage__detail" data-cjc-stage-detail aria-hidden="true">' +
          '<span class="cjc-stage__detail-dot" data-cjc-detail-dot></span>' +
          '<span class="cjc-stage__detail-card" data-cjc-detail-card>' +
            '<button type="button" class="cjc-stage__detail-x" data-cjc-detail-close aria-label="Close detail view">×</button>' +
            '<img data-cjc-detail-img alt="Option detail">' +
            '<i data-cjc-detail-cap></i>' +
          '</span>' +
        '</div>' +
      '</div>' +
      '<div class="cjc-stage__badges" data-cjc-stage-badges></div>';
    stage = {
      base: mediaEl.querySelector('[data-cjc-stage-base]'),
      fallback: mediaEl.querySelector('[data-cjc-stage-fallback]'),
      path: mediaEl.querySelector('[data-cjc-stage-path]'),
      stitch: mediaEl.querySelector('[data-cjc-stage-stitch]'),
      fallbackArt: mediaEl.querySelector('[data-cjc-stage-fallback-art]'),
      art: mediaEl.querySelector('[data-cjc-stage-art]'),
      mono: mediaEl.querySelector('[data-cjc-stage-mono]'),
      monoText: mediaEl.querySelector('[data-cjc-stage-mono-text]'),
      monoTag: mediaEl.querySelector('[data-cjc-stage-mono-tag]'),
      badges: mediaEl.querySelector('[data-cjc-stage-badges]'),
      detail: mediaEl.querySelector('[data-cjc-stage-detail]'),
      detailDot: mediaEl.querySelector('[data-cjc-detail-dot]'),
      detailCard: mediaEl.querySelector('[data-cjc-detail-card]'),
      detailImg: mediaEl.querySelector('[data-cjc-detail-img]'),
      detailCap: mediaEl.querySelector('[data-cjc-detail-cap]'),
      loadedShape: null,
      baseData: null
    };
    mediaEl.addEventListener('click', function (ev) {
      if (ev.target.closest('[data-cjc-detail-close]')) hideDetailInset();
    });
    stage.base.crossOrigin = 'anonymous';
    stage.base.addEventListener('load', function () {
      stage.base.style.display = 'block';
      stage.fallback.style.display = 'none';
      analyzeBaseImage();
      updateArtOverlay();
      updateMonoOverlay();
    });
    stage.base.addEventListener('error', function () {
      stage.base.style.display = 'none';
      stage.fallback.style.display = 'flex';
      stage.baseData = null;
      updateArtOverlay();
      updateMonoOverlay();
    });
  }

  function analyzeBaseImage() {
    stage.baseData = null;
    var img = stage.base;
    if (!img.naturalWidth) return;
    try {
      var SW = 96;
      var sh = Math.max(1, Math.round(SW * img.naturalHeight / img.naturalWidth));
      var c = document.createElement('canvas');
      c.width = SW; c.height = sh;
      var ctx = c.getContext('2d');
      ctx.drawImage(img, 0, 0, SW, sh);
      var d = ctx.getImageData(0, 0, SW, sh).data;
      var minX = SW, minY = sh, maxX = -1, maxY = -1, opaque = 0, transparent = 0;
      for (var y = 0; y < sh; y++) {
        for (var x = 0; x < SW; x++) {
          var a = d[(y * SW + x) * 4 + 3];
          if (a > 40) {
            opaque++;
            if (x < minX) minX = x; if (x > maxX) maxX = x;
            if (y < minY) minY = y; if (y > maxY) maxY = y;
          } else transparent++;
        }
      }
      var total = SW * sh;
      if (maxX < 0 || transparent < total * 0.05 || opaque < total * 0.05) return;
      stage.baseData = {
        w: img.naturalWidth, h: img.naturalHeight, hasAlpha: true,
        bounds: {
          x: minX / SW, y: minY / sh,
          w: (maxX - minX + 1) / SW, h: (maxY - minY + 1) / sh
        }
      };
    } catch (e) { stage.baseData = null; }
  }

  function updateStage() {
    if (!stage) return;
    var shape = (state.selections.leg_shape || 'leg_straight').replace('leg_', '');
    var washId = state.selections.wash || 'wash_medium';
    if (ASSET_BASE) {
      var src = ASSET_BASE + 'cj-base-' + shape + '.png';
      if (stage.loadedShape !== shape) { stage.loadedShape = shape; stage.base.src = src; }
    } else {
      stage.fallback.style.display = 'flex';
    }
    stage.base.style.filter = WASH_FILTERS[washId] || 'none';
    stage.path.setAttribute('fill', WASH_TINTS[washId] || '#4a6da7');
    var threadCol = THREAD_COLORS[state.selections.thread] || '#d4941e';
    stage.stitch.setAttribute('stroke', threadCol);
    updateArtOverlay();
    updateMonoOverlay();
    updateBadges();
  }

  function artPlacement(a) {
    var pl = ART_PLACEMENTS[0];
    ART_PLACEMENTS.forEach(function (p) { if (p.id === a.placement) pl = p; });
    return pl;
  }

  function updateArtOverlay() {
    if (!stage) return;
    var opt = selectedArtOption();
    var a = state.artwork;
    var photoMode = stage.base.style.display !== 'none' && stage.base.naturalWidth > 0;
    if (!a || !opt || !opt.artwork) {
      stage.art.style.display = 'none';
      if (stage.fallbackArt) stage.fallbackArt.style.display = 'none';
      return;
    }
    var pl = artPlacement(a);
    if (photoMode && stage.baseData && stage.baseData.hasAlpha) {
      if (stage.fallbackArt) stage.fallbackArt.style.display = 'none';
      compositeArtwork(a, pl);
      return;
    }
    if (!photoMode && stage.fallbackArt) {
      stage.art.style.display = 'none';
      var GX = 10, GY = 4, GW = 44, GH = 88;
      var w = GW * pl.gw * (a.scale / 100);
      var h = w * (a.h / a.w);
      var cx = GX + GW * pl.gx, cy = GY + GH * pl.gy;
      stage.fallbackArt.setAttribute('href', a.displayUrl);
      stage.fallbackArt.setAttribute('x', (cx - w / 2).toFixed(2));
      stage.fallbackArt.setAttribute('y', (cy - h / 2).toFixed(2));
      stage.fallbackArt.setAttribute('width', w.toFixed(2));
      stage.fallbackArt.setAttribute('height', h.toFixed(2));
      stage.fallbackArt.style.display = 'block';
      return;
    }
    if (stage.fallbackArt) stage.fallbackArt.style.display = 'none';
    stage.art.className = 'cjc-stage__art';
    stage.art.src = a.displayUrl;
    stage.art.style.display = 'block';
    stage.art.style.left = pl.x + '%';
    stage.art.style.top = pl.y + '%';
    stage.art.style.width = (pl.w * a.scale / 100) + '%';
    stage.art.style.opacity = a.filter === 'laser' ? 0.92 : 0.97;
  }

  function compositeArtwork(a, pl) {
    var img = stage.base;
    var bd = stage.baseData;
    try {
      var W = Math.min(900, img.naturalWidth);
      var H = Math.round(W * img.naturalHeight / img.naturalWidth);
      var b = { x: bd.bounds.x * W, y: bd.bounds.y * H, w: bd.bounds.w * W, h: bd.bounds.h * H };
      var artImg = new Image();
      artImg.onload = function () {
        var rw = b.w * pl.gw * (a.scale / 100);
        var rh = rw * (artImg.height / artImg.width);
        var maxH = b.h * (pl.id === 'full_leg' ? 0.86 : 0.42);
        if (rh > maxH) { rh = maxH; rw = rh * (artImg.width / artImg.height); }
        var cx = b.x + b.w * pl.gx, cy = b.y + b.h * pl.gy;
        var rx = cx - rw / 2, ry = cy - rh / 2;
        var artOnly = document.createElement('canvas');
        artOnly.width = W; artOnly.height = H;
        var actx = artOnly.getContext('2d');
        var STRIPS = 24, CURVE = 0.10, SHRINK = 0.12;
        for (var i = 0; i < STRIPS; i++) {
          var sx = artImg.width / STRIPS * i;
          var sw = artImg.width / STRIPS;
          var t = (i + 0.5) / STRIPS - 0.5;
          var bow = (t * t) * 4;
          var dy = bow * rh * CURVE;
          var dh = rh * (1 - bow * SHRINK);
          actx.drawImage(artImg, sx, 0, sw, artImg.height,
            rx + rw / STRIPS * i, ry + dy, rw / STRIPS + 0.6, dh);
        }
        var comp = document.createElement('canvas');
        comp.width = W; comp.height = H;
        var cctx = comp.getContext('2d');
        cctx.drawImage(artOnly, 0, 0);
        if (a.filter !== 'laser') {
          cctx.globalCompositeOperation = 'multiply';
          cctx.globalAlpha = a.filter === 'dtg' ? 0.75 : 0.5;
          cctx.filter = WASH_FILTERS[state.selections.wash] || 'none';
          cctx.drawImage(img, 0, 0, W, H);
          cctx.filter = 'none';
          cctx.globalAlpha = 1;
          cctx.globalCompositeOperation = 'destination-in';
          cctx.drawImage(artOnly, 0, 0);
        }
        cctx.globalCompositeOperation = 'destination-in';
        cctx.drawImage(img, 0, 0, W, H);
        stage.art.className = 'cjc-stage__art cjc-stage__art--full';
        stage.art.src = comp.toDataURL('image/png');
        stage.art.style.display = 'block';
        stage.art.style.left = ''; stage.art.style.top = '';
        stage.art.style.width = ''; stage.art.style.opacity = 1;
      };
      artImg.src = a.displayUrl;
    } catch (e) {
      stage.baseData = null;
      updateArtOverlay();
    }
  }

  function updateMonoOverlay() {
    if (!stage) return;
    var txt = state.monogramText.trim();
    if (!txt) { stage.mono.style.display = 'none'; return; }
    var anchor = MONO_ANCHORS[state.monogramPlacement] || MONO_ANCHORS.mono_waistband;
    var threadCol = THREAD_COLORS[state.selections.thread] || '#d4941e';
    var left = anchor.x, top = anchor.y;
    if (stage.baseData && stage.baseData.hasAlpha && stage.base.naturalWidth > 0) {
      var box = containBox(stage.base);
      var bb = stage.baseData.bounds;
      left = (box.x + (bb.x + bb.w * anchor.gx) * box.w) * 100;
      top = (box.y + (bb.y + bb.h * anchor.gy) * box.h) * 100;
    }
    stage.mono.style.display = 'flex';
    stage.mono.style.left = left + '%';
    stage.mono.style.top = top + '%';
    stage.mono.style.fontSize = anchor.size + 'em';
    stage.monoText.textContent = txt;
    stage.monoText.style.color = threadCol;
    stage.monoTag.textContent = anchor.tag;
  }

  function containBox(imgEl) {
    var bw = imgEl.clientWidth || 1, bh = imgEl.clientHeight || 1;
    var ar = imgEl.naturalWidth / imgEl.naturalHeight;
    var boxAr = bw / bh;
    if (ar > boxAr) {
      var h = boxAr / ar;
      return { x: 0, y: (1 - h) / 2, w: 1, h: h };
    }
    var w = ar / boxAr;
    return { x: (1 - w) / 2, y: 0, w: w, h: 1 };
  }

  function updateBadges() {
    if (!stage) return;
    var noteworthy = ['fading', 'rips', 'crystals', 'studs', 'underlay', 'trims', 'waist_button'];
    var chips = [];
    noteworthy.forEach(function (gid) {
      var g = findGroup(gid);
      if (!g || groupLocked(g)) return;
      var o = findOption(g, state.selections[gid]);
      if (o && o.delta > 0) chips.push(o.label);
    });
    if (!chips.length) { stage.badges.innerHTML = ''; return; }
    stage.badges.innerHTML = '<span class="cjc-stage__badges-label">Also in your build:</span>' +
      chips.map(function (c) { return '<span class="cjc-stage__badge">' + esc(c) + '</span>'; }).join('');
  }

  var detailTimer = null;
  function hideDetailInset() {
    if (!stage || !stage.detail) return;
    stage.detail.classList.remove('is-visible');
    if (detailTimer) { clearTimeout(detailTimer); detailTimer = null; }
  }
  function showDetailInset(g, o) {
    if (!stage || !stage.detail || !ASSET_BASE) return;
    var a = DETAIL_ANCHORS[g.id];
    if (!a) return;
    var left = a.x, top = a.y;
    if (stage.baseData && stage.baseData.hasAlpha && stage.base.naturalWidth > 0 && stage.base.style.display !== 'none') {
      var box = containBox(stage.base);
      var bb = stage.baseData.bounds;
      left = (box.x + (bb.x + bb.w * a.gx) * box.w) * 100;
      top = (box.y + (bb.y + bb.h * a.gy) * box.h) * 100;
    }
    stage.detailDot.style.left = left + '%';
    stage.detailDot.style.top = top + '%';
    var cardRight = left < 50;
    stage.detailCard.style.left = cardRight ? 'auto' : '3%';
    stage.detailCard.style.right = cardRight ? '3%' : 'auto';
    stage.detailCard.style.top = Math.max(4, Math.min(56, top - 14)) + '%';
    stage.detailImg.onerror = hideDetailInset;
    stage.detailImg.src = ASSET_BASE + 'cj-opt-' + o.id + '.jpg';
    stage.detailCap.textContent = o.label;
    stage.detail.classList.add('is-visible');
    if (detailTimer) clearTimeout(detailTimer);
    detailTimer = setTimeout(hideDetailInset, 5200);
  }
  function maybeShowDetail(gid) {
    var g = findGroup(gid);
    if (!g) return;
    var o = findOption(g, state.selections[gid]);
    if (!o) return;
    if (gid === 'surface_art' && o.artwork && state.artwork) return;
    showDetailInset(g, o);
  }

  var ART_MAX_MB = 12;
  var ART_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];

  function filterLabel(f) {
    return { laser: 'Laser-etch emulation', dtg: 'Direct-ink print emulation',
      foil_gold: 'Gold foil emulation', foil_silver: 'Silver foil emulation' }[f] || f;
  }

  function processPixels(data, filter) {
    var i, r, g, b, a, lum;
    for (i = 0; i < data.length; i += 4) {
      r = data[i]; g = data[i + 1]; b = data[i + 2]; a = data[i + 3];
      lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
      if (filter === 'laser') {
        var etch = (1 - lum) * (a / 255);
        data[i] = 233; data[i + 1] = 226; data[i + 2] = 208;
        data[i + 3] = Math.round(etch * 230);
      } else if (filter === 'dtg') {
        if (r > 242 && g > 242 && b > 242) { data[i + 3] = 0; continue; }
        var lm = lum * 255;
        data[i] = Math.round((r * 0.92 + lm * 0.08) * 0.94);
        data[i + 1] = Math.round((g * 0.92 + lm * 0.08) * 0.94);
        data[i + 2] = Math.round((b * 0.92 + lm * 0.08) * 0.94);
      } else if (filter === 'foil_gold' || filter === 'foil_silver') {
        var ink = (lum < 0.88) && (a > 100);
        if (!ink) { data[i + 3] = 0; continue; }
        var gold = filter === 'foil_gold';
        var base_ = gold ? [206, 158, 52] : [188, 195, 207];
        var hi = gold ? [255, 226, 140] : [246, 249, 253];
        var mix = (1 - lum) * 0.55 + (((i / 4) % 7) / 7) * 0.18;
        data[i] = Math.round(base_[0] + (hi[0] - base_[0]) * mix);
        data[i + 1] = Math.round(base_[1] + (hi[1] - base_[1]) * mix);
        data[i + 2] = Math.round(base_[2] + (hi[2] - base_[2]) * mix);
        data[i + 3] = 245;
      }
    }
  }

  function renderArtworkToUrls(img, filter, cb) {
    var MAXD = 700;
    var scale = Math.min(1, MAXD / Math.max(img.naturalWidth || img.width, img.naturalHeight || img.height));
    var w = Math.max(1, Math.round((img.naturalWidth || img.width) * scale));
    var h = Math.max(1, Math.round((img.naturalHeight || img.height) * scale));
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    var ctx = c.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);
    var display;
    try {
      var idata = ctx.getImageData(0, 0, w, h);
      processPixels(idata.data, filter);
      var c2 = document.createElement('canvas');
      c2.width = w; c2.height = h;
      c2.getContext('2d').putImageData(idata, 0, 0);
      display = c2.toDataURL('image/png');
    } catch (e) { display = c.toDataURL('image/png'); }
    var TM = 320;
    var ts = Math.min(1, TM / Math.max(w, h));
    var tc = document.createElement('canvas');
    tc.width = Math.max(1, Math.round(w * ts)); tc.height = Math.max(1, Math.round(h * ts));
    var tctx = tc.getContext('2d');
    tctx.fillStyle = '#ffffff'; tctx.fillRect(0, 0, tc.width, tc.height);
    tctx.drawImage(img, 0, 0, tc.width, tc.height);
    var thumb = '';
    var quals = [0.72, 0.6, 0.5, 0.4, 0.3];
    for (var q = 0; q < quals.length; q++) {
      thumb = tc.toDataURL('image/jpeg', quals[q]);
      if (thumb.length < 60000) break;
    }
    cb(display, thumb, img.naturalWidth || img.width, img.naturalHeight || img.height);
  }

  function handleArtworkFile(file) {
    if (!file) return;
    if (ART_TYPES.indexOf(file.type) === -1) {
      setArtMsg('Unsupported file type — please upload PNG, JPG, WebP or SVG.', true); return;
    }
    if (file.size > ART_MAX_MB * 1024 * 1024) {
      setArtMsg('File is over ' + ART_MAX_MB + 'MB — please export a smaller version.', true); return;
    }
    var opt = selectedArtOption();
    var filter = (opt && opt.artwork) || 'dtg';
    var reader = new FileReader();
    reader.onload = function () {
      var img = new Image();
      img.onload = function () {
        renderArtworkToUrls(img, filter, function (display, thumb, w, h) {
          var prev = state.artwork || {};
          state.artwork = {
            name: file.name, type: file.type, w: w, h: h,
            origUrl: reader.result, displayUrl: display, thumb: thumb, filter: filter,
            placement: prev.placement || 'front_thigh', scale: prev.scale || 100
          };
          rerender();
        });
      };
      img.onerror = function () { setArtMsg('Could not read that image — try re-exporting it as PNG.', true); };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  }

  function refilterArtwork() {
    var a = state.artwork;
    var opt = selectedArtOption();
    if (!a || !opt || !opt.artwork || a.filter === opt.artwork) return;
    var img = new Image();
    img.onload = function () {
      renderArtworkToUrls(img, opt.artwork, function (display, thumb, w, h) {
        a.displayUrl = display; a.filter = opt.artwork; a.w = w; a.h = h;
        rerender();
      });
    };
    img.src = a.origUrl;
  }

  function setArtMsg(text, isErr) {
    var el = stepsEl.querySelector('[data-cjc-art-msg]');
    if (el) { el.textContent = text; el.className = 'cjc-art__msg' + (isErr ? ' is-error' : ''); }
  }

  function renderArtPanel(opt) {
    var a = state.artwork;
    var h = '<div class="cjc-art" data-cjc-art>';
    h += '<div class="cjc-art__head"><span class="cjc-art__title">Your artwork — live preview with ' + esc(filterLabel(opt.artwork).toLowerCase()) + '</span>';
    h += '<button type="button" class="cjc-info" data-cjc-modal="artguide" aria-label="Artwork guidelines and templates">i</button></div>';
    if (!a) {
      h += '<label class="cjc-art__drop" for="cjc-art-file">';
      h += '<strong>⬆ Upload your artwork file here</strong><span>This is where you add your own design — PNG, JPG, WebP or SVG, max ' + ART_MAX_MB + 'MB. It appears on the jeans preview instantly with the ' + esc(filterLabel(opt.artwork).toLowerCase()) + ', and you can reposition, resize or replace it.</span>';
      h += '</label>';
      h += '<input id="cjc-art-file" class="cjc-art__file" type="file" accept="' + ART_TYPES.join(',') + '" data-cjc-artfile>';
      h += '<p class="cjc-art__optional">No file ready? You can order without one — our studio proposes the ' + esc(opt.label.toLowerCase()) + ' design for your approval at the sampling stage.</p>';
    } else {
      h += '<div class="cjc-art__loaded">';
      h += '<img class="cjc-art__preview" src="' + a.displayUrl + '" alt="Artwork preview">';
      h += '<div class="cjc-art__meta"><strong>' + esc(a.name) + '</strong><span>' + a.w + ' × ' + a.h + ' px · shown with ' + esc(filterLabel(a.filter).toLowerCase()) + '</span>';
      h += '<span class="cjc-art__actions"><label class="cjc-chip cjc-chip--sm" for="cjc-art-file">Replace</label>';
      h += '<button type="button" class="cjc-chip cjc-chip--sm" data-cjc-artremove>Remove</button></span></div>';
      h += '<input id="cjc-art-file" class="cjc-art__file" type="file" accept="' + ART_TYPES.join(',') + '" data-cjc-artfile>';
      h += '</div>';
      h += '<div class="cjc-art__controls">';
      h += '<div class="cjc-art__placements"><span class="cjc-field__title">Placement</span><div>';
      ART_PLACEMENTS.forEach(function (p) {
        var sel = a.placement === p.id;
        var blocked = p.id === 'full_leg' && fullLegBlocked();
        h += '<button type="button" class="cjc-chip' + (sel ? ' is-selected' : '') + (blocked ? ' is-disabled' : '') + '" data-cjc-artplace="' + p.id + '"' + (blocked ? ' disabled title="Unavailable with knee destruction (Step 6) — the full-leg panel crosses the rip zone."' : '') + ' role="radio" aria-checked="' + sel + '">' + esc(p.label) + (blocked ? ' 🔒' : '') + '</button>';
      });
      h += '</div>';
      if (fullLegBlocked()) h += '<p class="cjc-art__placenote">Full-leg panel is unavailable while knee rips are selected in Step 6 — thigh and calf panels sit clear of the destruction zone.</p>';
      h += '</div>';
      h += '<label class="cjc-art__scale"><span class="cjc-field__title">Artwork size <em data-cjc-artscale-val>' + a.scale + '%</em></span>';
      h += '<input type="range" min="40" max="130" step="5" value="' + a.scale + '" data-cjc-artscale></label>';
      h += '</div>';
    }
    h += '<p class="cjc-art__msg" data-cjc-art-msg></p>';
    h += '</div>';
    return h;
  }

  function templateLink(key, label) {
    var url = TEMPLATES[key] || (ASSET_BASE ? ASSET_BASE + 'cj-print-template-' + key.replace(/_/g, '-') + '.svg' : '');
    if (!url) return '';
    return '<li><a href="' + url + '" download>' + esc(label) + ' template (SVG)</a></li>';
  }
  var MODALS = {
    artguide: function () {
      return '<h3>Artwork guidelines & templates</h3>' +
        '<p>What the factory can reproduce, and how to prepare your file:</p>' +
        '<ul>' +
        '<li><strong>Resolution:</strong> at least 150&nbsp;DPI at final print size (e.g. front thigh 22×28cm → min 1300×1650&nbsp;px).</li>' +
        '<li><strong>Laser etching:</strong> single-tone only — your design is burned into the indigo, so dark areas of your file become pale etched denim. Fine hairlines under 1&nbsp;mm may not survive.</li>' +
        '<li><strong>DTG / direct-ink print:</strong> full colour. Pure white in your file is treated as transparent background; if you need printed white, mention it in order notes (needs an underbase).</li>' +
        '<li><strong>Metallic foil:</strong> one colour (gold or silver). Any visible line-work becomes the foil stamp — solid shapes hold better than fine texture.</li>' +
        '<li><strong>Formats:</strong> PNG with transparent background works best; SVG, JPG and WebP accepted.</li>' +
        '</ul>' +
        '<p><strong>Print-area templates</strong> (drop your art onto the guide layer, export as PNG):</p>' +
        '<ul>' + templateLink('front_thigh', 'Front thigh 22×28cm') + templateLink('calf', 'Calf panel 18×22cm') + templateLink('full_leg', 'Full leg 30×75cm') + '</ul>' +
        '<p class="cjc-modal__small">The on-page preview is an emulation for design intent. The factory recreates it with production equipment and sends a photo sample for approval before the bulk run — nothing prints without your sign-off.</p>';
    },
    sizeguide: function () {
      var sys = sizeSystem();
      return '<h3>How the size-run breakdown works</h3>' +
        '<p>Your ' + state.qty + ' pcs are cut across a <strong>size curve</strong> — a quantity per size. The base-size pattern (defined above) is graded up and down using standard increments to produce each size.</p>' +
        '<ul>' +
        '<li><strong>Your own breakdown:</strong> you type the exact quantity per size. The total must equal your order quantity — the counter below the grid shows what’s left to allocate.</li>' +
        '<li><strong>Factory-balanced curve:</strong> we allocate using a proven bell-shaped retail curve for ' + esc(sys.label) + '. You approve it on the pro-forma invoice before cutting.</li>' +
        '</ul>' +
        '<p>Example for 300 pcs (balanced): ' + (function () {
          var run = distributeRun(300, sys.balanced);
          return sys.sizes.map(function (s, i) { return s + '×' + run[i]; }).join(', ');
        })() + '.</p>' +
        '<p class="cjc-modal__small">Not sure? Choose the factory-balanced curve — our production team confirms the final grading split with you before anything is cut.</p>';
    }
  };
  function openModal(key) {
    closeModal();
    var wrap = document.createElement('div');
    wrap.className = 'cjc-modal';
    wrap.setAttribute('data-cjc-modal-wrap', '');
    wrap.innerHTML = '<div class="cjc-modal__backdrop" data-cjc-modal-close></div>' +
      '<div class="cjc-modal__box" role="dialog" aria-modal="true">' +
      '<button type="button" class="cjc-modal__x" data-cjc-modal-close aria-label="Close">×</button>' +
      MODALS[key]() + '</div>';
    document.body.appendChild(wrap);
    wrap.addEventListener('click', function (ev) {
      if (ev.target.closest('[data-cjc-modal-close]')) closeModal();
    });
  }
  function closeModal() {
    var m = document.querySelector('[data-cjc-modal-wrap]');
    if (m) m.parentNode.removeChild(m);
  }
  document.addEventListener('keydown', function (ev) { if (ev.key === 'Escape') closeModal(); });

  function renderOptionButton(g, o) {
    var sel = state.selections[g.id] === o.id;
    var restricted = optionRestricted(g, o);
    var html = '<button type="button" class="cjc-opt' + (sel ? ' is-selected' : '') + (restricted ? ' is-restricted' : '') + '"' +
      (restricted ? ' disabled aria-disabled="true"' : '') +
      ' role="radio" aria-checked="' + sel + '" data-group="' + g.id + '" data-option="' + o.id + '">';
    if (ASSET_BASE) {
      html += '<span class="cjc-opt__thumb"><img src="' + ASSET_BASE + 'cj-opt-' + o.id + '.jpg" alt="" decoding="async" onerror="this.parentNode.classList.add(&quot;is-missing&quot;)" onload="this.parentNode.classList.add(&quot;is-loaded&quot;)"></span>';
    }
    if (g.type === 'swatches') {
      html += '<span class="cjc-opt__swatch" style="background:' + o.swatch + '"></span>';
    } else if (o.svg) {
      html += legSvg(o.svg);
    }
    html += '<span class="cjc-opt__body"><span class="cjc-opt__label">' + esc(o.label) + '</span>';
    if (o.desc) html += '<span class="cjc-opt__desc">' + esc(o.desc) + '</span>';
    if (restricted) {
      html += '<span class="cjc-opt__restricted">🚫 ' + esc(o.notForReason || 'Not available with the selected Body Geometry.') + '</span>';
    } else {
      if (o.artwork) html += '<span class="cjc-opt__upnote">works with your own artwork — upload it in the panel below</span>';
      var hint = conflictHint(g, o);
      if (hint) html += '<span class="cjc-opt__conflict">⚠ ' + esc(hint) + '</span>';
    }
    html += deltaTag(o.delta) + '</span></button>';
    return html;
  }

  function renderMonogram(g) {
    var chars = state.monogramText.length;
    var h = '<div class="cjc-mono">';
    h += '<label class="cjc-mono__label" for="cjc-mono-input">Type your name / initials / phrase <span>(max ' + g.maxChars + ' characters — leave blank for none)</span></label>';
    h += '<div class="cjc-mono__row"><input id="cjc-mono-input" class="cjc-mono__input" type="text" maxlength="' + g.maxChars + '" value="' + esc(state.monogramText) + '" placeholder="e.g. GEMS №1" data-cjc-mono>';
    h += '<span class="cjc-mono__count" data-cjc-mono-count>' + chars + '/' + g.maxChars + '</span></div>';
    h += '<div class="cjc-mono__placements" role="radiogroup" aria-label="Monogram placement">';
    g.placements.forEach(function (p) {
      var sel = state.monogramPlacement === p.id;
      h += '<button type="button" class="cjc-chip' + (sel ? ' is-selected' : '') + '" data-cjc-placement="' + p.id + '" role="radio" aria-checked="' + sel + '">' + esc(p.label) + '</button>';
    });
    h += '</div>';
    h += '<p class="cjc-mono__note">' + (state.monogramText.trim() ? 'Embroidered monogram +' + money(g.delta) + '/pc — shown live on the preview (thread colour follows Step 4).' : 'No monogram — no charge. Start typing to see it on the preview.') + '</p>';
    h += '</div>';
    return h;
  }

  function renderSizeRun() {
    var sys = sizeSystem();
    var h = '<div class="cjc-run" data-cjc-run>';
    h += '<div class="cjc-run__head"><span class="cjc-field__title">Size-run breakdown across your ' + state.qty + ' pcs</span>';
    h += '<button type="button" class="cjc-info" data-cjc-modal="sizeguide" aria-label="How the size run works">i</button></div>';
    h += '<p class="cjc-run__hint">Every production run is cut across a size curve (' + esc(sys.label) + '). Type your exact split — or let the proven factory curve allocate it for you.</p>';
    h += '<div class="cjc-run__modes">';
    h += '<button type="button" class="cjc-method cjc-method--slim' + (state.sizeRunMode === 'custom' ? ' is-selected' : '') + '" data-cjc-runmode="custom" role="radio" aria-checked="' + (state.sizeRunMode === 'custom') + '">';
    h += '<span class="cjc-method__name">My own breakdown</span><span class="cjc-method__desc">Type the exact quantity per size — must total ' + state.qty + ' pcs.</span></button>';
    h += '<button type="button" class="cjc-method cjc-method--slim' + (state.sizeRunMode === 'factory' ? ' is-selected' : '') + '" data-cjc-runmode="factory" role="radio" aria-checked="' + (state.sizeRunMode === 'factory') + '">';
    h += '<span class="cjc-method__name">Factory-balanced curve</span><span class="cjc-method__desc">Proven retail bell curve, allocated for you and confirmed before cutting.</span></button>';
    h += '</div>';
    if (state.sizeRunMode === 'factory') {
      var run = factoryRun();
      h += '<div class="cjc-run__table cjc-run__table--ro">';
      sys.sizes.forEach(function (s) {
        h += '<span class="cjc-run__cell"><b>' + esc(s) + '</b><i>' + run[s] + '</i></span>';
      });
      h += '</div>';
      h += '<p class="cjc-run__status is-ok" data-cjc-run-status>✓ ' + state.qty + ' pcs allocated automatically — adjusts live with your quantity.</p>';
    } else {
      h += '<div class="cjc-run__presets">Quick fill: ';
      h += '<button type="button" class="cjc-chip cjc-chip--sm" data-cjc-runpreset="balanced">Balanced curve</button>';
      h += '<button type="button" class="cjc-chip cjc-chip--sm" data-cjc-runpreset="core">Core-size weighted</button>';
      h += '<button type="button" class="cjc-chip cjc-chip--sm" data-cjc-runpreset="clear">Clear</button>';
      h += '</div>';
      h += '<div class="cjc-run__table">';
      sys.sizes.forEach(function (s) {
        var v = state.sizeRunQty[s];
        h += '<label class="cjc-run__cell"><b>' + esc(s) + '</b>';
        h += '<input type="number" min="0" step="1" inputmode="numeric" value="' + (v === undefined || v === '' ? '' : esc(v)) + '" data-cjc-runsize="' + esc(s) + '"></label>';
      });
      h += '</div>';
      var sum = customRunTotal();
      var diff = state.qty - sum;
      var cls = diff === 0 && sum > 0 ? 'is-ok' : 'is-warn';
      var msg = diff === 0 && sum > 0 ? '✓ All ' + state.qty + ' pcs allocated.'
        : diff > 0 ? sum + ' of ' + state.qty + ' pcs allocated — assign ' + diff + ' more.'
        : sum + ' allocated — remove ' + (-diff) + ' pcs (order is ' + state.qty + ').';
      h += '<p class="cjc-run__status ' + cls + '" data-cjc-run-status>' + msg + '</p>';
    }
    h += '</div>';
    return h;
  }

  function renderSizing() {
    var h = '<div class="cjc-sizing">';
    h += '<p class="cjc-sizing__framing">You’re specifying fit for a <strong>' + state.qty + '-piece production run</strong>, not a single pair: first set the <strong>base-size pattern</strong> below, then spread the quantity across sizes. The factory grades every size from your base pattern.</p>';
    h += '<div class="cjc-sizing__methods">';
    SIZING_METHODS.forEach(function (m) {
      var sel = state.sizingMethod === m.id;
      h += '<button type="button" class="cjc-method' + (sel ? ' is-selected' : '') + '" data-cjc-method="' + m.id + '" role="radio" aria-checked="' + sel + '">';
      h += '<span class="cjc-method__name">' + esc(m.label) + '</span>';
      h += '<span class="cjc-method__desc">' + esc(m.desc) + '</span></button>';
    });
    h += '</div>';
    var m = null;
    SIZING_METHODS.forEach(function (x) { if (x.id === state.sizingMethod) m = x; });
    if (m) {
      h += '<div class="cjc-sizing__form">';
      if (m.fields) {
        h += '<div class="cjc-sizing__units">Units: ';
        ['cm', 'in'].forEach(function (u) {
          h += '<button type="button" class="cjc-chip cjc-chip--sm' + (state.sizingUnit === u ? ' is-selected' : '') + '" data-cjc-unit="' + u + '">' + u + '</button>';
        });
        h += '</div><div class="cjc-sizing__grid">';
        m.fields.forEach(function (f) {
          var v = state.sizingFields[f.id] || '';
          h += '<label class="cjc-field"><span>' + esc(f.label) + ' (' + state.sizingUnit + ')</span>';
          h += '<input type="number" min="0" step="0.5" inputmode="decimal" value="' + esc(v) + '" data-cjc-sizefield="' + f.id + '"></label>';
        });
        h += '</div>';
      }
      if (m.selects) {
        h += '<div class="cjc-sizing__grid cjc-sizing__grid--selects">';
        m.selects.forEach(function (s) {
          var v = state.sizingFields[s.id] || '';
          h += '<label class="cjc-field"><span>' + esc(s.label) + '</span><select data-cjc-sizefield="' + s.id + '">';
          h += '<option value="">Select…</option>';
          s.options.forEach(function (op) { h += '<option value="' + op + '"' + (v === op ? ' selected' : '') + '>' + op + '</option>'; });
          h += '</select></label>';
        });
        h += '</div>';
      }
      if (m.choice) {
        var cv = state.sizingFields[m.choice.id] || m.choice.options[m.choice.id === 'fit_pref' ? 1 : 0];
        state.sizingFields[m.choice.id] = cv;
        h += '<div class="cjc-sizing__choice"><span class="cjc-field__title">' + esc(m.choice.label) + '</span><div>';
        m.choice.options.forEach(function (op) {
          h += '<button type="button" class="cjc-chip' + (cv === op ? ' is-selected' : '') + '" data-cjc-sizechoice="' + m.choice.id + '" data-value="' + esc(op) + '">' + esc(op) + '</button>';
        });
        h += '</div></div>';
      }
      h += renderSizeRun();
      h += '</div>';
    } else {
      h += '<p class="cjc-sizing__prompt">Select a fit method to continue — required before placing the order.</p>';
    }
    h += '</div>';
    return h;
  }

  var openStep = 1;
  var STEP_SHORT = ['Shape', 'Denim', 'Hardware', 'Stitching', 'Pockets', 'Distress', 'Prints', 'Sparkle', 'Trims', 'Personal', 'Fit & sizes'];

  function stepComplete(step) {
    if (step.num === 11) return sizingValid();
    return !!state.touched['step' + step.num];
  }

  function renderSteps() {
    var step = CATALOG[openStep - 1] || CATALOG[0];
    var h = '<section class="cjc-panel" data-step="' + step.num + '">';
    h += '<p class="cjc-panel__eyebrow">Step ' + step.num + ' of ' + CATALOG.length + '</p>';
    h += '<h3 class="cjc-panel__title">' + esc(step.title) + '</h3>';
    h += '<p class="cjc-panel__sub">' + esc(step.sub) + '</p>';
    if (state.adjustNotes && state.adjustNotes.step === step.num) {
      h += '<div class="cjc-notice" role="status">';
      h += '<strong>Build adjusted for production feasibility:</strong>';
      state.adjustNotes.msgs.forEach(function (m) { h += '<span>' + esc(m) + '</span>'; });
      h += '</div>';
    }
    step.groups.forEach(function (g) {
      var locked = groupLocked(g);
      h += '<div class="cjc-group' + (locked ? ' is-locked' : '') + '" data-group-wrap="' + g.id + '">';
      h += '<div class="cjc-group__label">' + esc(g.label);
      if (locked) h += ' <span class="cjc-group__lock">🔒 ' + esc(g.lockedBy.message) + '</span>';
      h += '</div>';
      if (g.type === 'monogram') h += renderMonogram(g);
      else if (g.type === 'sizing') h += renderSizing();
      else {
        h += '<div class="cjc-group__options" role="radiogroup" aria-label="' + esc(g.label) + '">';
        g.options.forEach(function (o) { h += renderOptionButton(g, o); });
        h += '</div>';
        if (g.id === 'surface_art') {
          var opt = findOption(g, state.selections.surface_art);
          if (opt && opt.artwork) h += renderArtPanel(opt);
        }
      }
      h += '</div>';
    });
    h += '<footer class="cjc-panel__nav">';
    if (step.num > 1) {
      h += '<button type="button" class="cjc-panel__back" data-cjc-back>← Back</button>';
    } else {
      h += '<span></span>';
    }
    if (step.num < 11) {
      h += '<button type="button" class="cjc-panel__next" data-cjc-next="' + step.num + '">Confirm & continue →</button>';
    } else {
      h += '<button type="button" class="cjc-panel__next" data-cjc-next="11">Confirm build — review &amp; order ↓</button>';
    }
    h += '</footer></section>';
    stepsEl.innerHTML = h;
  }

  function renderStepper() {
    if (!stepperEl) return;
    var h = '';
    CATALOG.forEach(function (step) {
      var current = openStep === step.num;
      var done = stepComplete(step);
      h += '<button type="button" class="cjc-stepnum' + (current ? ' is-current' : '') + (done ? ' is-done' : '') + '"';
      h += ' data-cjc-goto="' + step.num + '" role="tab" aria-selected="' + current + '" title="Step ' + step.num + ': ' + esc(step.title) + '">';
      h += '<i>' + (done && !current ? '✓' : step.num) + '</i>';
      h += '<em>' + esc(STEP_SHORT[step.num - 1] || step.num) + '</em>';
      h += '</button>';
    });
    stepperEl.innerHTML = h;
  }

  function stepSummary(step) {
    var parts = [];
    step.groups.forEach(function (g) {
      if (g.type === 'monogram') {
        parts.push(state.monogramText.trim() ? '“' + state.monogramText.trim() + '”' : 'No monogram');
      } else if (g.type === 'sizing') {
        var m = null;
        SIZING_METHODS.forEach(function (x) { if (x.id === state.sizingMethod) m = x; });
        parts.push(m ? m.label : 'Not set');
        if (m) parts.push(state.sizeRunMode === 'factory' ? 'Factory curve' : 'Custom size run');
      } else if (g.options) {
        if (groupLocked(g)) return;
        var o = findOption(g, state.selections[g.id]);
        if (o) parts.push(o.label);
        if (g.id === 'surface_art' && o && o.artwork) {
          parts.push(state.artwork ? 'Artwork: ' + state.artwork.name : 'No artwork uploaded yet');
        }
      }
    });
    return parts.join(' · ');
  }

  function renderSummary() {
    var h = '';
    CATALOG.forEach(function (step) {
      h += '<button type="button" class="cjc-sumrow" data-cjc-jump="' + step.num + '">';
      h += '<span class="cjc-sumrow__step">' + step.num + '</span>';
      h += '<span class="cjc-sumrow__text"><strong>' + esc(step.title) + '</strong><em>' + esc(stepSummary(step)) + '</em></span>';
      h += '<span class="cjc-sumrow__edit">edit ›</span>';
      h += '</button>';
    });
    summaryEl.innerHTML = h;
  }

  function renderPrice() {
    var p = computePrice();
    var moqOk = state.qty >= MOQ;
    var h = '<div class="cjc-price__row"><span>Base garment</span><span>' + money(p.base) + '/pc</span></div>';
    p.lines.forEach(function (l) {
      h += '<div class="cjc-price__row cjc-price__row--delta"><span>' + esc(l.label) + '</span><span>+' + money(l.delta) + '</span></div>';
    });
    if (p.tier.off > 0) {
      h += '<div class="cjc-price__row cjc-price__row--tier"><span>Volume tier ' + p.tier.qty + '+ pcs</span><span>−' + p.tier.off + '%</span></div>';
    }
    h += '<div class="cjc-price__unit"><span>Per piece</span><strong>' + money(p.unit) + '</strong></div>';
    h += '<div class="cjc-price__total"><span>Order total × ' + state.qty + ' pcs</span><strong>' + money(p.total) + '</strong></div>';
    h += '<div class="cjc-price__deposit"><span>' + DEPOSIT_PCT + '% deposit (pro-forma invoice option)</span><span>' + money(p.deposit) + '</span></div>';
    if (!moqOk) {
      h += '<p class="cjc-price__moq">Minimum order is ' + MOQ + ' pcs — add ' + (MOQ - state.qty) + ' more.</p>';
    }
    priceEl.innerHTML = h;
    if (mobileBar) {
      mobileBar.querySelector('[data-cjc-m-unit]').textContent = money(p.unit) + '/pc';
      mobileBar.querySelector('[data-cjc-m-total]').textContent = money(p.total);
    }
    updateCta();
  }

  function renderProgress() {
    var done = 0;
    CATALOG.forEach(function (s) { if (stepComplete(s)) done++; });
    var pct = Math.round(done / CATALOG.length * 100);
    if (progressFill) progressFill.style.width = pct + '%';
    if (progressText) progressText.textContent = done + ' of ' + CATALOG.length + ' steps confirmed';
    renderStepper();
  }

  function sizingValid() {
    var m = null;
    SIZING_METHODS.forEach(function (x) { if (x.id === state.sizingMethod) m = x; });
    if (!m) return false;
    if (m.fields) {
      for (var i = 0; i < m.fields.length; i++) {
        var v = parseFloat(state.sizingFields[m.fields[i].id]);
        if (isNaN(v) || v <= 0) return false;
      }
    }
    if (m.selects) {
      for (var j = 0; j < m.selects.length; j++) {
        if (!state.sizingFields[m.selects[j].id]) return false;
      }
    }
    return sizeRunValid();
  }

  function updateCta() {
    var ok = state.qty >= MOQ && sizingValid();
    addBtn.disabled = !ok;
    var hint = root.querySelector('[data-cjc-cta-hint]');
    if (hint) {
      if (state.qty < MOQ) hint.textContent = 'Set quantity to at least ' + MOQ + ' pcs to order.';
      else if (!sizingValid()) {
        var mSel = !!state.sizingMethod;
        if (mSel && !sizeRunValid()) hint.textContent = 'Size run: ' + customRunTotal() + ' of ' + state.qty + ' pcs allocated — finish the breakdown in Step 11.';
        else hint.textContent = 'Complete Step 11 (Production Fit & Size Curve) to unlock ordering.';
      }
      else hint.textContent = PROFORMA ? 'Spec complete — place your order (no payment due online).' : 'Spec complete — ready to add to cart.';
    }
  }

  function rerender() {
    hideDetailInset();
    renderSteps();
    renderSummary();
    renderPrice();
    renderProgress();
    updateStage();
  }

  function injectResetButton() {
    var btn = root.querySelector('[data-cjc-reset]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      if (!window.confirm('Start over? This clears every selection, uploaded artwork and sizing — the page will not reload.')) return;
      state = freshState();
      openStep = 1;
      if (qtyInput) qtyInput.value = MOQ;
      if (msgEl) { msgEl.textContent = ''; msgEl.className = 'cjc-msg'; }
      if (stage) stage.loadedShape = null;
      rerender();
      var heroEl = root.querySelector('.cjc-hero');
      if (heroEl) heroEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  function buildProperties() {
    var props = {};
    var backendSpec = { style_ref: cfg.productTitle || 'Custom Jeans', configured_at: new Date().toISOString(), moq: MOQ, quantity: state.qty, options: {} };
    CATALOG.forEach(function (step) {
      step.groups.forEach(function (g) {
        var keyPrefix = (step.num < 10 ? '0' : '') + step.num + ' · ';
        if (g.type === 'monogram') {
          var txt = state.monogramText.trim();
          props[keyPrefix + 'Custom Monogramming'] = txt ? txt : 'None';
          if (txt) {
            var pl = '';
            g.placements.forEach(function (p) { if (p.id === state.monogramPlacement) pl = p.label; });
            props[keyPrefix + 'Monogram Placement'] = pl;
            backendSpec.options.custom_embroidery = { text: txt, placement: pl };
          } else {
            backendSpec.options.custom_embroidery = null;
          }
          return;
        }
        if (g.type === 'sizing') {
          var m = null;
          SIZING_METHODS.forEach(function (x) { if (x.id === state.sizingMethod) m = x; });
          if (!m) return;
          props[keyPrefix + 'Fit Method'] = m.label;
          var sizing = { method: m.backend, unit: state.sizingUnit, fields: {} };
          (m.fields || []).forEach(function (f) {
            props[keyPrefix + f.label] = state.sizingFields[f.id] + ' ' + state.sizingUnit;
            sizing.fields[f.id] = parseFloat(state.sizingFields[f.id]);
          });
          (m.selects || []).forEach(function (s) {
            props[keyPrefix + s.label] = state.sizingFields[s.id];
            sizing.fields[s.id] = state.sizingFields[s.id];
          });
          if (m.choice) {
            props[keyPrefix + m.choice.label] = state.sizingFields[m.choice.id];
            sizing.fields[m.choice.id] = state.sizingFields[m.choice.id];
          }
          var sys = sizeSystem();
          var run = activeRun();
          var runStr = sys.sizes.filter(function (s) { return run[s]; })
            .map(function (s) { return s + '×' + run[s]; }).join(', ');
          props[keyPrefix + 'Size Run (' + (state.sizeRunMode === 'factory' ? 'factory-balanced curve' : 'buyer breakdown') + ')'] = runStr;
          sizing.size_run = { mode: state.sizeRunMode === 'factory' ? 'factory-balanced' : 'custom',
            system: sys.label, total: state.qty, breakdown: run };
          backendSpec.options.sizing = sizing;
          return;
        }
        if (!g.options) return;
        if (groupLocked(g)) {
          backendSpec.options[g.id] = { backend: 'N/A (locked — requires rip selection)', label: 'N/A' };
          return;
        }
        var o = findOption(g, state.selections[g.id]);
        if (!o) return;
        props[keyPrefix + g.label] = o.label + (o.delta > 0 ? ' (+' + money(o.delta) + '/pc)' : '');
        backendSpec.options[g.id] = { backend_group: g.backend, backend: o.backend, label: o.label, surcharge_per_pc: o.delta };
        if (g.id === 'surface_art' && o.artwork) {
          if (state.artwork) {
            var a = state.artwork;
            var plLabel = '', plBackend = '';
            ART_PLACEMENTS.forEach(function (p) { if (p.id === a.placement) { plLabel = p.label; plBackend = p.backend; } });
            props[keyPrefix + 'Print Artwork'] = a.name + ' (' + a.w + '×' + a.h + 'px · ' + plLabel + ' · ' + a.scale + '% scale)';
            backendSpec.options.surface_art.artwork = {
              filename: a.name, mime: a.type, width_px: a.w, height_px: a.h,
              process_emulation: a.filter, placement: plBackend, scale_pct: a.scale,
              note: 'Preview thumbnail attached; request print-ready original from buyer before sampling.',
              thumb_data_url: a.thumb
            };
          } else {
            props[keyPrefix + 'Print Artwork'] = 'None uploaded — studio pattern to be proposed at sampling';
            backendSpec.options.surface_art.artwork = null;
            backendSpec.options.surface_art.artwork_note = 'No buyer artwork uploaded — studio to propose ' + o.backend + ' design for buyer approval at sampling stage.';
          }
        }
      });
    });
    var p = computePrice();
    props['Estimated per-piece price'] = money(p.unit);
    props['Estimated order total'] = money(p.total) + ' (' + state.qty + ' pcs)';
    props['Deposit due (' + DEPOSIT_PCT + '%)'] = money(p.deposit);
    if (PROFORMA) {
      props['Payment terms'] = 'No payment collected online — pro-forma invoice follows: ' + DEPOSIT_PCT + '% deposit to start production, balance before shipping. Payable by bank transfer.';
    }
    backendSpec.pricing = { base_per_pc: p.base, surcharge_per_pc: p.surcharge, tier_discount_pct: p.tier.off,
      unit_price: Math.round(p.unit * 100) / 100, order_total: Math.round(p.total * 100) / 100, currency: cfg.currencyCode || 'GBP' };
    props['_Manufacturer Spec JSON'] = JSON.stringify(backendSpec);
    return { props: props, price: p };
  }

  function addToCart() {
    var payload = buildProperties();
    var lineVariant = PROFORMA ? ORDER_VARIANT_ID : VARIANT_ID;
    var items = [{ id: parseInt(lineVariant, 10), quantity: state.qty, properties: payload.props }];
    var surchargeUnits = Math.round(payload.price.surcharge * state.qty);
    if (!PROFORMA && SURCHARGE_VARIANT_ID && surchargeUnits > 0) {
      items.push({
        id: parseInt(SURCHARGE_VARIANT_ID, 10), quantity: surchargeUnits,
        properties: { 'For style': cfg.productTitle || 'Custom Jeans', 'Covers': 'Customisation surcharges @ ' + money(payload.price.surcharge) + '/pc × ' + state.qty + ' pcs' }
      });
    }

    if (PREVIEW) {
      console.log('JGP PREVIEW — /cart/add.js payload:', JSON.stringify({ items: items }, null, 2));
      msgEl.textContent = 'Preview mode: payload logged to console (' + Object.keys(payload.props).length + ' properties).';
      msgEl.className = 'cjc-msg cjc-msg--ok';
      return;
    }

    addBtn.disabled = true;
    addBtn.textContent = PROFORMA ? 'Placing order…' : 'Adding…';
    /* Pro-forma mode: the production order MUST be the sole checkout line so
       the total is £0 and no payment is requested. Clear any leftover cart
       items first (e.g. a garment added during an earlier test), then add the
       single £0 request line. Regular mode leaves the cart untouched. */
    var prep = PROFORMA
      ? fetch('/cart/clear.js', { method: 'POST' })
      : Promise.resolve();
    prep
      .then(function () {
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items })
        });
      })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw e; });
        return r.json();
      })
      .then(function () {
        if (PROFORMA) {
          msgEl.textContent = 'Spec saved — confirm your contact details to receive the pro-forma invoice…';
          msgEl.className = 'cjc-msg cjc-msg--ok';
          window.location.href = '/checkout';
        } else {
          msgEl.textContent = 'Production order added to cart — redirecting…';
          msgEl.className = 'cjc-msg cjc-msg--ok';
          window.location.href = '/cart';
        }
      })
      .catch(function (err) {
        msgEl.textContent = (err && err.description) ? err.description : 'Could not submit the order — please try again.';
        msgEl.className = 'cjc-msg cjc-msg--error';
        addBtn.textContent = PROFORMA ? 'Place Production Order' : 'Add production order to cart';
        updateCta();
      });
  }

  stepsEl.addEventListener('click', function (ev) {
    var t = ev.target;
    var next = t.closest('[data-cjc-next]');
    if (next) {
      var sn = parseInt(next.getAttribute('data-cjc-next'), 10);
      state.touched['step' + sn] = true;
      if (sn < 11) {
        openStep = sn + 1;
        rerender();
        scrollToStep(openStep);
      } else {
        rerender();
        var pb = root.querySelector('.cjc-pricebox');
        if (pb) pb.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    var back = t.closest('[data-cjc-back]');
    if (back) {
      if (openStep > 1) { openStep--; rerender(); scrollToStep(openStep); }
      return;
    }
    var info = t.closest('[data-cjc-modal]');
    if (info) { openModal(info.getAttribute('data-cjc-modal')); return; }
    var opt = t.closest('.cjc-opt');
    if (opt) {
      var gid = opt.getAttribute('data-group');
      var og = findGroup(gid);
      var oClicked = findOption(og, opt.getAttribute('data-option'));
      if (oClicked && optionRestricted(og, oClicked)) return;
      state.selections[gid] = opt.getAttribute('data-option');
      CATALOG.forEach(function (s) {
        s.groups.forEach(function (x) { if (x.id === gid) state.touched['step' + s.num] = true; });
      });
      if (gid === 'rips' && state.selections.rips === 'rips_none') state.selections.underlay = 'under_none';
      if (gid === 'body_geometry') state.sizeRunQty = {};
      var adjustments = (gid === 'body_geometry') ? applyCutRestrictions() : [];
      adjustments = adjustments.concat(applyConflicts(gid));
      state.adjustNotes = adjustments.length ? { step: stepNumOf(gid), msgs: adjustments } : null;
      if (gid === 'surface_art') refilterArtwork();
      rerender();
      maybeShowDetail(gid);
      return;
    }
    var chip = t.closest('[data-cjc-placement]');
    if (chip) { state.monogramPlacement = chip.getAttribute('data-cjc-placement'); rerender(); return; }
    var unit = t.closest('[data-cjc-unit]');
    if (unit) { state.sizingUnit = unit.getAttribute('data-cjc-unit'); rerender(); return; }
    var method = t.closest('[data-cjc-method]');
    if (method) { state.sizingMethod = method.getAttribute('data-cjc-method'); rerender(); return; }
    var schoice = t.closest('[data-cjc-sizechoice]');
    if (schoice) {
      state.sizingFields[schoice.getAttribute('data-cjc-sizechoice')] = schoice.getAttribute('data-value');
      rerender();
      return;
    }
    var runmode = t.closest('[data-cjc-runmode]');
    if (runmode) { state.sizeRunMode = runmode.getAttribute('data-cjc-runmode'); rerender(); return; }
    var preset = t.closest('[data-cjc-runpreset]');
    if (preset) {
      var kind = preset.getAttribute('data-cjc-runpreset');
      var sys = sizeSystem();
      state.sizeRunQty = {};
      if (kind !== 'clear') {
        var weights = kind === 'core' ? sys.core : sys.balanced;
        var run = distributeRun(state.qty, weights);
        sys.sizes.forEach(function (s, i) { state.sizeRunQty[s] = run[i]; });
      }
      rerender();
      return;
    }
    var artplace = t.closest('[data-cjc-artplace]');
    if (artplace) {
      if (state.artwork) { state.artwork.placement = artplace.getAttribute('data-cjc-artplace'); rerender(); }
      return;
    }
    var artremove = t.closest('[data-cjc-artremove]');
    if (artremove) { state.artwork = null; rerender(); return; }
  });

  stepsEl.addEventListener('input', function (ev) {
    var t = ev.target;
    if (t.hasAttribute('data-cjc-mono')) {
      state.monogramText = t.value;
      var count = stepsEl.querySelector('[data-cjc-mono-count]');
      if (count) count.textContent = t.value.length + '/12';
      renderSummary(); renderPrice(); renderProgress(); updateMonoOverlay();
      var note = stepsEl.querySelector('.cjc-mono__note');
      if (note) note.textContent = t.value.trim() ? 'Embroidered monogram applied — see the live preview.' : 'No monogram — no charge. Start typing to see it on the preview.';
      return;
    }
    if (t.hasAttribute('data-cjc-sizefield')) {
      state.sizingFields[t.getAttribute('data-cjc-sizefield')] = t.value;
      renderSummary(); renderPrice(); renderProgress();
      return;
    }
    if (t.hasAttribute('data-cjc-runsize')) {
      state.sizeRunQty[t.getAttribute('data-cjc-runsize')] = t.value;
      updateRunStatusLive();
      renderProgress(); updateCta();
      return;
    }
    if (t.hasAttribute('data-cjc-artscale')) {
      if (state.artwork) {
        state.artwork.scale = parseInt(t.value, 10) || 100;
        var lbl = stepsEl.querySelector('[data-cjc-artscale-val]');
        if (lbl) lbl.textContent = state.artwork.scale + '%';
        updateArtOverlay();
      }
      return;
    }
  });

  stepsEl.addEventListener('change', function (ev) {
    var t = ev.target;
    if (t.hasAttribute('data-cjc-sizefield')) {
      state.sizingFields[t.getAttribute('data-cjc-sizefield')] = t.value;
      renderSummary(); renderPrice(); renderProgress();
      return;
    }
    if (t.hasAttribute('data-cjc-artfile')) {
      if (t.files && t.files[0]) handleArtworkFile(t.files[0]);
      return;
    }
  });

  function updateRunStatusLive() {
    var el = stepsEl.querySelector('[data-cjc-run-status]');
    if (!el || state.sizeRunMode !== 'custom') return;
    var sum = customRunTotal();
    var diff = state.qty - sum;
    var ok = diff === 0 && sum > 0;
    el.className = 'cjc-run__status ' + (ok ? 'is-ok' : 'is-warn');
    el.textContent = ok ? '✓ All ' + state.qty + ' pcs allocated.'
      : diff > 0 ? sum + ' of ' + state.qty + ' pcs allocated — assign ' + diff + ' more.'
      : sum + ' allocated — remove ' + (-diff) + ' pcs (order is ' + state.qty + ').';
  }

  summaryEl.addEventListener('click', function (ev) {
    var row = ev.target.closest('[data-cjc-jump]');
    if (!row) return;
    openStep = parseInt(row.getAttribute('data-cjc-jump'), 10);
    rerender();
    scrollToStep(openStep);
  });

  if (stepperEl) {
    stepperEl.addEventListener('click', function (ev) {
      var btn = ev.target.closest('[data-cjc-goto]');
      if (!btn) return;
      openStep = parseInt(btn.getAttribute('data-cjc-goto'), 10);
      rerender();
      scrollToStep(openStep);
    });
  }

  function scrollToStep() {
    var el = stepsEl.querySelector('.cjc-panel');
    if (!el) return;
    var top = el.getBoundingClientRect().top;
    if (top < 0 || top > window.innerHeight * 0.5) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  if (qtyInput) {
    qtyInput.value = MOQ;
    qtyInput.min = MOQ;
    qtyInput.step = QTY_STEP;
    qtyInput.addEventListener('input', function () {
      var v = parseInt(qtyInput.value, 10);
      state.qty = isNaN(v) ? 0 : v;
      renderPrice();
      if (openStep === 11) { renderSteps(); renderProgress(); }
    });
    qtyInput.addEventListener('blur', function () {
      if (state.qty < MOQ) { state.qty = MOQ; qtyInput.value = MOQ; renderPrice(); if (openStep === 11) renderSteps(); }
    });
  }

  addBtn.addEventListener('click', addToCart);

  root.querySelectorAll('[data-cjc-tierbtn]').forEach(function (b) {
    b.addEventListener('click', function () {
      state.qty = parseInt(b.getAttribute('data-cjc-tierbtn'), 10);
      if (qtyInput) qtyInput.value = state.qty;
      renderPrice();
      if (openStep === 11) { renderSteps(); renderProgress(); }
    });
  });

  buildStage();
  injectResetButton();
  rerender();
})();
